"use client";

import { useState } from "react";
import { MessageCircle, Send, Phone, Mail, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";

const Contacto = () => {
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    asunto: "general",
    mensaje: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value: string) => {
    setFormData({ ...formData, asunto: value });
  };

  const handleWhatsAppSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const asuntoTexto = {
      general: "Consulta general",
      anunciante: "Información para anunciantes",
      colaborador: "Quiero ser colaborador",
      otro: "Otro asunto",
    }[formData.asunto] || "Consulta";

    const mensaje = `*Nuevo contacto desde M4B Web*%0A%0A*Nombre:* ${formData.nombre}%0A*Email:* ${formData.email}%0A*Asunto:* ${asuntoTexto}%0A*Mensaje:* ${formData.mensaje}`;

    const whatsappNumber = "34614230720";
    window.open(`https://wa.me/${whatsappNumber}?text=${mensaje}`, "_blank");
  };

  return (
    <section id="contacto" className="section-padding bg-m4b-light">
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Info side */}
          <div>
            <span className="text-primary text-sm font-medium tracking-[0.2em] uppercase mb-4 block">
              Contacto
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-m4b-dark mb-6 leading-tight">
              Hablemos de tu
              <br />
              <span className="text-primary">proyecto</span>
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-10">
              ¿Tienes alguna pregunta? Escríbenos y te responderemos por WhatsApp lo antes posible.
            </p>

            {/* Contact info */}
            <div className="space-y-6">
              {[
                { icon: Phone, label: "WhatsApp", value: "+34 614 230 720" },
                { icon: Mail, label: "Email", value: "info@xappiens.com" },
                { icon: MapPin, label: "Ubicación", value: "Cartagena (Murcia), España" },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg">
                    <item.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{item.label}</p>
                    <p className="font-medium text-m4b-dark">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form side */}
          <Card className="border-border/50 shadow-lg">
            <CardContent className="p-8">
              <form onSubmit={handleWhatsAppSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre *</Label>
                  <Input
                    type="text"
                    id="nombre"
                    name="nombre"
                    required
                    value={formData.nombre}
                    onChange={handleChange}
                    placeholder="Tu nombre"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="tu@email.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="asunto">Asunto</Label>
                  <Select value={formData.asunto} onValueChange={handleSelectChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona un asunto" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">Consulta general</SelectItem>
                      <SelectItem value="anunciante">Quiero anunciarme</SelectItem>
                      <SelectItem value="colaborador">Quiero colaborar</SelectItem>
                      <SelectItem value="otro">Otro asunto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mensaje">Mensaje *</Label>
                  <Textarea
                    id="mensaje"
                    name="mensaje"
                    required
                    rows={4}
                    value={formData.mensaje}
                    onChange={handleChange}
                    placeholder="¿En qué podemos ayudarte?"
                    className="resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold shadow-md transition-all duration-300 hover:scale-[1.02]"
                >
                  <MessageCircle className="w-5 h-5" />
                  Enviar por WhatsApp
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contacto;
